import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryKeys } from '../../../api/queryKeys.ts';
import { useAuth } from '../components/AuthContext.tsx';

import { acceptOxsTerms, fetchOxsTerms, type OxsTerms } from './oxsTerms.ts';

type UseOxsTermsReturn = {
  /** `null` for a non-OXS user, `undefined` while loading or after a failed lookup */
  terms: OxsTerms | null | undefined;
  /** a failed lookup keeps the T&C required (an error with retry is shown instead) */
  showTerms: boolean;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  retry: () => void;
  /**
   * Writes the acceptance unless it is already saved, so the original acceptance date is kept.
   * Rejects when the T&C state is unavailable.
   */
  accept: () => Promise<void>;
};

export const useOxsTerms = (isAiConnector: boolean): UseOxsTermsReturn => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [QueryKeys.LOGIN_APP_OXS_TERMS, currentUser?.uid], [currentUser?.uid]);

  const { data, isError, isFetching, isPending, refetch } = useQuery({
    // the inline Alert reports the failure, so skip the global error snackbar
    meta: { disableNotification: true },
    queryFn: isAiConnector && currentUser ? () => fetchOxsTerms(currentUser) : skipToken,
    queryKey,
    retry: false,
  });

  // React Query keeps the previous data when a refetch fails; ignore it so the error never leaves T&C accepted
  const terms = isError ? undefined : data;

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  const accept = useCallback(async () => {
    if (!terms) {
      // a failed or pending lookup must block the consent instead of skipping the write
      throw new Error('OXS T&C state is unavailable');
    }
    if (!currentUser || terms.accepted) {
      return;
    }

    await acceptOxsTerms(currentUser, terms.userId);
    // so a retry after a failed consent doesn't write (and re-date) the acceptance again
    queryClient.setQueryData<OxsTerms>(queryKey, { ...terms, accepted: true });
  }, [currentUser, terms, queryClient, queryKey]);

  return {
    accept,
    isError,
    isFetching,
    // a skipped (disabled) query also reports `isPending`
    isPending: isAiConnector && !!currentUser && isPending,
    retry,
    showTerms: isAiConnector && terms !== null,
    terms,
  };
};
